import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiClient, ApiError } from './api-client';
import type { UserRole } from './types';

export interface SafeUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  groupIds: string[];
}

interface AuthContextValue {
  user: SafeUser | null;
  loading: boolean;
  isAdmin: boolean;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SafeUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = async () => {
    // skipAuth: avoid the apiClient's 401-retry-then-redirect chain on bootstrap.
    // If user has no session, /auth/me returns 401 — we just set user=null instead of redirecting.
    try {
      const me = await apiClient.get<SafeUser>('/auth/me', { skipAuth: true });
      setUser(me);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    void refreshMe().finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string): Promise<string | null> => {
    try {
      await apiClient.post('/auth/login', { email, password }, { skipAuth: true });
      const me = await apiClient.get<SafeUser>('/auth/me');
      setUser(me);
      return null;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        return 'Nesprávný email nebo heslo';
      }
      return 'Přihlášení selhalo';
    }
  };

  const logout = async () => {
    try { await apiClient.post('/auth/logout', {}); } catch { /* ignore */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      isAdmin: user?.role === 'admin',
      isLoggedIn: !!user,
      login, logout, refreshMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
